import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { queryKeys } from '@/shared/api/queryKeys';
import { useRoleStore } from './roleStore';
import { changePassword, fetchSession, signIn, signOut } from './sessionApi';

export function useSession() {
  return useQuery({ queryKey: queryKeys.session, queryFn: fetchSession, staleTime: Infinity, retry: false });
}

export function useSignIn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => signIn(email, password),
    onSuccess: (user) => {
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== queryKeys.session[0] }); // nothing cached from another user
      client.setQueryData(queryKeys.session, user);
      useRoleStore.getState().setRole(user.role); // start as the account's role; the switcher can change it
    },
  });
}

export function useChangePassword() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ current, next }: { current: string; next: string }) => changePassword(current, next),
    onSuccess: (user) => { client.setQueryData(queryKeys.session, user); },
  });
}

export function useSignOut() {
  const client = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: signOut,
    onSettled: () => {
      client.clear();
      client.setQueryData(queryKeys.session, null);
      void navigate('/login', { replace: true });
    },
  });
}
